import { Directive, ElementRef, Input, Renderer2, ViewChild } from '@angular/core';
import type { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { MatLegacySelect as MatSelect } from '@angular/material/legacy-select';
import { AbstractFormFieldComponent } from '@shared/form-fields/abstract-form-field/abstract-form-field.component';
import type { AppSelectOption } from '@shared/form-fields/options/select-option';

@Directive()
export class BaseMultiSelectFormFieldComponent<T> extends AbstractFormFieldComponent<T[]> {
    @ViewChild('selectComponent') selectComponent: MatSelect;

    @Input() placeholder: string;

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('compareWith') _compareWith: (a: AppSelectOption<T>, b: AppSelectOption<T>) => boolean;
    @Input() protected objectEqualityKey = 'id';
    @Input() allowOnly = true;

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('options') protected _options: AppSelectOption<T>[];
    @Input() maxSelectedShowing = 3;
    protected _apiOptions: AppSelectOption<T>[];

    @Input() allowSelectAll = true;
    @Input() allowDeselectAll = true;

    @Input() only: T[];

    @Input() fetching = false;

    get options(): AppSelectOption<T>[] {
        const options = this._apiOptions || this._options;

        if (options && this.only) {
            return options.filter(option => this.only.findIndex(i => this.compareValueWith(i, option.value)) > -1);
        }

        return options;
    }

    protected set apiOptions(options: AppSelectOption<T>[]) {
        this._apiOptions = options;

        if (this._selectedOptions) {
            this._selectedOptions = this._selectedOptions
                .map(selectedOption =>
                    this.options.find(o => this.compareWith(o, selectedOption))
                )
                // Filter it in case the selected option is not available in the options array; i.e., it's been passed to the component as a pre-selected option but is not available in the options array
                .filter(Boolean);
        }
    }

    private _selectedOptions: AppSelectOption<T>[];

    get selectedOptions(): AppSelectOption<T>[] {
        return this._selectedOptions;
    }

    set selectedOptions(value: AppSelectOption<T>[]) {
        this._selectedOptions = value;

        this.value = value ? value.map(o => o.value) : [];
    }

    /**
     * Read several times per change detection by templates, and the team selector has hundreds
     * of options all selected, so this is one pass with a keyed lookup rather than comparing
     * every option with every selection.
     */
    get allOptionsSelected(): boolean {
        if (!this.options || !this.selectedOptions) {
            return false;
        }

        return this.everyOptionSelected(this.options, this.selectedOptions);
    }

    get selectedEnabledOptions(): AppSelectOption<T>[] {
        return [...this.selectedOptions].filter(o => this.isOptionEnabled(o));
    }

    get enabledOptions(): AppSelectOption<T>[] {
        return [...this.options ?? []].filter(o => this.isOptionEnabled(o));
    }

    get disabledOptions(): AppSelectOption<T>[] {
        return [...this.options ?? []].filter(o => this.isOptionDisabled(o));
    }

    constructor(element: ElementRef, renderer: Renderer2) {
        super(element, renderer);

        this.compareWith = this.compareWith.bind(this);
    }

    writeValue(value: T[]) {
        super.writeValue(value);

        if (value) {
            this._selectedOptions = value.map(v => ({
                label: undefined,
                value: v
            }));
        } else {
            this._selectedOptions = [];
        }

        if (this._selectedOptions && this.options) {
            this._selectedOptions = this._selectedOptions
                .map(selectedOption =>
                    this.options.find(o => this.compareWith(o, selectedOption))
                )
                // Filter it in case the selected option is not available in the options array; i.e., it's been passed to the component as a pre-selected option but is not available in the options array
                .filter(Boolean);
        }
    }

    public compareWith(a: AppSelectOption<T>, b: AppSelectOption<T>): boolean {
        if (this._compareWith) {
            return this._compareWith(a, b);
        }

        if (a === b) {
            return true;
        }

        if (a && b) {
            const aValue = a.value;
            const bValue = b.value;

            return this.compareValueWith(aValue, bValue);
        }

        return false;
    }

    private compareValueWith(aValue: T, bValue: T) {
        return (
            aValue === bValue ||
            (typeof aValue === 'object' &&
                typeof bValue === 'object' &&
                aValue[this.objectEqualityKey] === bValue[this.objectEqualityKey])
        );
    }

    onSelectionChange($event: MatSelectChange) {
        console.log({ $event });
    }

    clear($event: MouseEvent) {
        super.clear($event);

        this.selectedOptions = [];
    }

    get selectedOptionsLabel(): string {
        return (this.selectedOptions || [])
            .filter(Boolean)
            .map(o => o.label)
            .filter(Boolean)
            .join(', ');
    }

    get triggerLabel(): string {
        return null;
    }

    selectAll(): void {
        this.selectedOptions = [...this.enabledOptions];
        this.selectComponent?.close();
    }

    deselectAll(): void {
        this.selectedOptions = [...this.selectedOptions.filter(o => this.isOptionDisabled(o))];
    }

    isOptionDisabled(option: AppSelectOption<T>): boolean {
        // Override in child class to implement allow options to be disabled
        return false;
    }

    isOptionEnabled(options: AppSelectOption<T>): boolean {
        return !this.isOptionDisabled(options);
    }

    get allEnabledOptionsSelected(): boolean {
        return this.everyOptionSelected(this.enabledOptions, this.selectedOptions ?? []);
    }

    /**
     * Whether each option matches a selection, by the same rules as compareWith. A custom
     * compareWith can't be keyed, so it keeps the pairwise comparison.
     */
    private everyOptionSelected(options: AppSelectOption<T>[], selectedOptions: AppSelectOption<T>[]): boolean {
        if (this._compareWith) {
            return options.every(option => selectedOptions.some(o => this.compareWith(o, option)));
        }

        const selected = new SelectedValueIndex<T>(this.objectEqualityKey);

        for (const selectedOption of selectedOptions) {
            selected.add(selectedOption);
        }

        return options.every(option => selected.has(option));
    }

    selectOne($event: MouseEvent, option: AppSelectOption<T>) {
        $event.preventDefault();
        $event.stopPropagation();
        $event.stopImmediatePropagation();

        if (!this.isOptionDisabled(option)) {
            const selectedOptions = [...this.selectedOptions].filter(o => this.isOptionDisabled(o));

            this.selectedOptions = [...selectedOptions, option];
            this.selectComponent?.close();
        }
    }
}

/**
 * The selected options, indexed so an option can be matched in constant time. Matches as
 * compareWith does without a custom comparison: the same option, the same value, or object
 * values with the same equality key.
 *
 * Two edge cases differ. A null value never matches an object here, where compareWith throws
 * reading the key from null. NaN matches NaN, as a Set compares values.
 */
export class SelectedValueIndex<T> {
    private readonly options = new Set<AppSelectOption<T>>();
    private readonly values = new Set<T>();
    private readonly objectKeys = new Set<unknown>();

    constructor(private readonly objectEqualityKey: string) {}

    add(selectedOption: AppSelectOption<T>): void {
        this.options.add(selectedOption);

        // compareWith only compares values when both options are set
        if (!selectedOption) {
            return;
        }

        this.values.add(selectedOption.value);

        if (isObject(selectedOption.value)) {
            this.objectKeys.add(selectedOption.value[this.objectEqualityKey]);
        }
    }

    has(option: AppSelectOption<T>): boolean {
        if (this.options.has(option)) {
            return true;
        }

        if (!option) {
            return false;
        }

        if (this.values.has(option.value)) {
            return true;
        }

        if (isObject(option.value)) {
            return this.objectKeys.has(option.value[this.objectEqualityKey]);
        }

        return false;
    }
}

function isObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}
